import { Paperclip, X } from 'lucide-react';
import { useRef } from 'react';

import { IMAGE_ACCEPT, useImageAttachments } from 'pages/Dashboard/useImageAttachments';
import { AttachmentSkeleton } from 'pages/Dashboard/AttachmentLoading';
import { ImageAttachmentPreview } from 'pages/Dashboard/ImageAttachmentPreview';
import { Tooltip } from 'components';

type ImageAttachmentProps = {
	images: ReturnType<typeof useImageAttachments>;
	disabled?: boolean;
};

export function ImageUploadButton({
	images,
	disabled = false,
	circular = false,
}: ImageAttachmentProps & { circular?: boolean }) {
	const inputRef = useRef<HTMLInputElement>(null);
	return (
		<>
			<Tooltip content='Attach images or PDFs' triggerType='span' disableHelpCursor>
				<button
					type='button'
					aria-label='Upload images or PDFs'
					disabled={disabled || images.uploading || images.files.length >= 4}
					onClick={() => inputRef.current?.click()}
					className={`flex h-8 items-center justify-center text-blue-500 hover:bg-blue-100/80 dark:text-blue-400 dark:hover:bg-blue-700/80 disabled:opacity-50 disabled:cursor-not-allowed ${
						circular ? 'w-8 rounded-full' : 'w-10 rounded-xl'
					}`}
				>
					<Paperclip size={18} />
				</button>
			</Tooltip>
			<input
				ref={inputRef}
				type='file'
				multiple
				accept={IMAGE_ACCEPT}
				className='hidden'
				disabled={disabled || images.uploading}
				onChange={(event) => {
					images.add(Array.from(event.target.files || []));
					event.target.value = '';
				}}
			/>
		</>
	);
}

export function ImageAttachmentControls({ images, disabled = false }: ImageAttachmentProps) {
	// Keep the empty composer compact; the upload button lives in its existing controls row.
	if (!images.files.length && !images.error && !images.uploading) return null;
	return (
		<div className='flex flex-wrap items-center gap-2 px-3 py-2'>
			{images.files.map((file) => (
				<div key={file.id} className='relative rounded-lg border border-gray-200'>
					<ImageAttachmentPreview src={file.preview} name={file.name} type={file.type} />
					{images.uploadingId === file.id && <AttachmentSkeleton label={`Uploading ${file.name}`} />}
					<button
						type='button'
						disabled={disabled || images.uploading}
						aria-label={`Remove ${file.name}`}
						className='absolute -top-1 -right-1 rounded-full bg-gray-600 p-1 text-white hover:bg-red-600 disabled:opacity-50'
						onClick={() => images.remove(file.id)}
					>
						<X size={16} />
					</button>
				</div>
			))}
			{images.error && <p role='alert'>{images.error}</p>}
			{images.uploading && <p role='status'>Uploading attachments…</p>}
		</div>
	);
}
